import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Folder, Upload, Loader, CheckCircle2, XCircle, ChevronRight, FolderOpen, FileArchive, Check, Download, Link2, LayoutGrid, List } from 'lucide-react';
import { FileThumb, getFileCategory } from '../components/FileThumb';
import { FilePreviewPanel } from '../components/FilePreviewPanel';
import { supabase } from '../lib/supabase';
import { generateVideoFrameDataUrl } from '../utils/video/thumbnail';
import JSZip from 'jszip';

interface Breadcrumb {
  id: string;
  name: string;
}

interface FolderInfo {
  id: string;
  name: string;
}

interface ShareFile {
  id: string;
  name: string;
  size_bytes: number;
  mime_type: string | null;
  storage_path: string;
  created_at: string;
}

interface UploadedFileInfo {
  name: string;
  size_bytes: number;
  thumbnail?: string | null;
}

interface ShareInfo {
  folderName: string;
  allowUpload: boolean;
  maxFolderSizeBytes: number;
  maxFileSizeBytes: number;
  showUploadBox: boolean;
  currentFolderId: string;
  breadcrumbs: Breadcrumb[];
  folders: FolderInfo[];
  files: ShareFile[];
  allFiles: ShareFile[];
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const PublicFileUpload: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<ShareFile | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Optional uploader name, remembered locally so repeat visitors don't have
  // to retype it. Included in the batch notification email when present.
  const [uploaderName, setUploaderName] = useState(() => {
    try { return localStorage.getItem('shareUploaderName') || ''; } catch { return ''; }
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadInfo = useCallback(async (folderId: string | null) => {
    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const params = new URLSearchParams({ token: token || '' });
      if (folderId) params.set('folder_id', folderId);
      const res = await fetch(`${supabaseUrl}/functions/v1/storage-public-upload?${params.toString()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.error || 'Link not found');
        return;
      }
      const data = await res.json();
      setInfo(data);
      setCurrentFolderId(data.currentFolderId);
      setError(null);
    } catch (err) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    loadInfo(null);
  }, [token, loadInfo]);

  // Returns the uploaded file's name + size on success, or null on failure,
  // so a multi-file batch can be collected and reported in a single
  // notification email instead of one per file.
  const uploadFile = async (file: File, targetFolderId: string): Promise<UploadedFileInfo | null> => {
    if (!token) return null;
    setUploading(true);
    setUploadError(null);
    setDone(null);
    setProgress(5);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const formData = new FormData();
      formData.append('token', token);
      formData.append('folder_id', targetFolderId);
      formData.append('file', file);

      // Simulate progress while uploading (edge functions don't stream progress)
      const interval = setInterval(() => {
        setProgress(p => Math.min(p + Math.random() * 8, 90));
      }, 150);

      const res = await fetch(`${supabaseUrl}/functions/v1/storage-public-upload`, {
        method: 'POST',
        body: formData,
      });
      clearInterval(interval);

      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setUploadError(body?.error || 'Upload failed');
        setProgress(0);
        return null;
      }

      setProgress(100);
      setDone(`${body?.name || file.name} uploaded`);
      // Refresh the current folder so the uploaded file appears
      loadInfo(targetFolderId);

      // Best-effort preview for the notification email: images use their
      // own public URL, videos get a small first-frame JPEG grabbed
      // client-side (the edge function can't decode video on the server).
      let thumbnail: string | null = null;
      try {
        if (file.type.startsWith('image/')) {
          thumbnail = body?.publicUrl || null;
        } else if (file.type.startsWith('video/')) {
          thumbnail = await generateVideoFrameDataUrl(file);
        }
      } catch (thumbErr) {
        console.error('Failed to generate preview thumbnail:', thumbErr);
      }

      return { name: body?.name || file.name, size_bytes: file.size, thumbnail };
    } catch (err) {
      setUploadError('Connection failed');
      setProgress(0);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Sends one notification email for the whole batch of files uploaded in
  // this action (single file, multi-select, or drag & drop of several
  // files). Best-effort — a failure here must never block/disrupt the
  // upload itself, so errors are only logged.
  const notifyUploadBatch = async (uploaded: UploadedFileInfo[]) => {
    if (!token || uploaded.length === 0) return;
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      await fetch(`${supabaseUrl}/functions/v1/notify-shared-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, files: uploaded, uploader_name: uploaderName.trim() || null }),
      });
    } catch (err) {
      console.error('Failed to send upload notification email:', err);
    }
  };

  const handleUploaderNameChange = (value: string) => {
    setUploaderName(value);
    try { localStorage.setItem('shareUploaderName', value); } catch { /* ignore */ }
  };

  const handleFiles = async (fileList: FileList | File[], targetFolderId: string) => {
    const uploaded: UploadedFileInfo[] = [];
    for (const file of Array.from(fileList)) {
      const result = await uploadFile(file, targetFolderId);
      if (result) uploaded.push(result);
    }
    await notifyUploadBatch(uploaded);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!info?.allowUpload || !info.showUploadBox) {
      setUploadError(info?.allowUpload
        ? 'Uploads are not available in this area.'
        : 'Uploads are not allowed for this link.');
      window.setTimeout(() => setUploadError(null), 3000);
      return;
    }
    if (e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files, currentFolderId || '');
    }
  };

  const getFileUrl = (file: ShareFile): string => {
    return `https://${new URL(import.meta.env.VITE_SUPABASE_URL).host}/storage/v1/object/public/file-storage/${file.storage_path}`;
  };

  const downloadSingleFile = async (file: ShareFile) => {
    const { data } = supabase.storage.from('file-storage').getPublicUrl(file.storage_path, { download: file.name });
    const a = document.createElement('a');
    a.href = data.publicUrl;
    a.download = file.name;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copyFileLink = async (file: ShareFile) => {
    const url = getFileUrl(file);
    const showMsg = `Link of ${file.name} copied`;
    try {
      await navigator.clipboard.writeText(url);
      setDone(showMsg);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); setDone(showMsg); } catch { /* ignore */ }
      document.body.removeChild(textarea);
    }
    setTimeout(() => setDone(null), 2500);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedFiles = (info?.files || []).filter(f => selectedIds.has(f.id));
  const downloadableFiles = info?.allFiles?.length ? info.allFiles : (info?.files || []);

  const downloadSelectedAsZip = async () => {
    if (selectedFiles.length === 0) return;
    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      for (const file of selectedFiles) {
        const res = await fetch(getFileUrl(file));
        if (!res.ok) continue;
        const blob = await res.blob();
        zip.file(file.name, blob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'files.zip';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('ZIP error:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const downloadAllAsZip = async () => {
    if (!info || downloadableFiles.length === 0) return;
    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      for (const file of downloadableFiles) {
        const res = await fetch(getFileUrl(file));
        if (!res.ok) continue;
        const blob = await res.blob();
        zip.file(file.name, blob);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${info.folderName}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('ZIP error:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  if (loading && !info) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Invalid link</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{error || 'The link was not found or has expired.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
            <Folder className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{info.folderName}</h1>
            <p className="text-xs text-gray-600 dark:text-gray-400">Shared folder</p>
          </div>
          <div className="flex items-center bg-gray-100 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          {selectedFiles.length >= 2 ? (
            <>
              <button
                onClick={downloadSelectedAsZip}
                disabled={downloadingZip}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed !px-3 !py-2"
              >
                {downloadingZip ? <Loader className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
                ZIP ({selectedFiles.length})
              </button>
              <button
                onClick={clearSelection}
                className="btn-outline !px-3 !py-2"
              >
                Clear selection
              </button>
            </>
          ) : (
            downloadableFiles.length > 0 && (
              <button
                onClick={downloadAllAsZip}
                disabled={downloadingZip}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed !px-3 !py-2"
              >
                {downloadingZip ? <Loader className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
                Download All
              </button>
            )
          )}
        </div>

        {/* Status messages */}
        {done && (
          <div className="mb-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{done}</span>
          </div>
        )}
        {uploadError && (
          <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="mb-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 mb-2">
              <span className="flex items-center gap-2"><Loader className="w-3 h-3 animate-spin text-primary-500" /> Uploading...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="flex gap-4 items-start">
          <div className="flex-1 min-w-0">
            {/* Optional uploader name — remembered locally, included in the notification email */}
            {info.allowUpload && info.showUploadBox && (
              <div className="mb-3">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Your name (optional)</label>
                <input
                  type="text"
                  value={uploaderName}
                  onChange={(e) => handleUploaderNameChange(e.target.value)}
                  placeholder="e.g. Max"
                  className="w-full max-w-xs px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            {/* Breadcrumbs — only shown when inside a subfolder of the share */}
            {info.breadcrumbs.length > 1 && (
              <div className="flex items-center gap-1 mb-3 flex-wrap">
                {info.breadcrumbs.map((crumb, i) => (
                  <React.Fragment key={crumb.id}>
                    {i > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <button
                      onClick={() => loadInfo(i === 0 ? null : crumb.id)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${i === info.breadcrumbs.length - 1 ? 'text-primary-600 dark:text-white bg-primary-50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    >
                      {i === 0 ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                      {crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Content + dropzone (no outer frame; highlight only while dragging) */}
            <div
              className={`rounded-xl min-h-[300px] transition-colors ${
                dragging ? 'bg-primary-50 dark:bg-primary-900/20' : ''
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                if (info.allowUpload && info.showUploadBox) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              {info.folders.length === 0 && info.files.length === 0 ? (
                info.allowUpload && info.showUploadBox ? (
                  <div
                    className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400 cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className={`w-10 h-10 mb-3 ${dragging ? 'text-primary-500' : 'text-gray-400'}`} />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Drag & drop files here</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">or click to select files</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-500">
                    <p className="text-sm">This folder is empty.</p>
                  </div>
                )
              ) : (
                <div className="p-4">
                  {viewMode === 'grid' ? (
                    <div className="grid [grid-template-columns:repeat(auto-fill,minmax(150px,150px))] justify-start gap-3">
                    {info.folders.map(folder => (
                      <button
                        key={folder.id}
                        onClick={() => loadInfo(folder.id)}
                        className="group bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-left hover:border-primary-500 transition-all"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center mb-2">
                          <Folder className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Folder</p>
                      </button>
                    ))}
                    {info.files.map(file => {
                      const isSelected = selectedIds.has(file.id);
                      const isPreviewing = previewFile?.id === file.id;
                      return (
                        <div
                          key={file.id}
                          onClick={() => setPreviewFile(file)}
                          className={`group relative bg-white dark:bg-dark-800 border rounded-lg overflow-hidden cursor-pointer transition-all hover:border-primary-500 ${isSelected ? 'border-primary-500' : isPreviewing ? 'border-gray-400' : 'border-gray-200 dark:border-gray-700'}`}
                        >
                          <div className="relative">
                            <FileThumb file={file} size="md" />
                            <div className="absolute top-2 right-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleSelect(file.id); }}
                                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                  isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'bg-white dark:bg-gray-900/80 border-gray-400 dark:border-gray-500 text-transparent hover:border-primary-500'
                                }`}
                                title={isSelected ? 'Clear selection' : 'Select'}
                              >
                                <Check className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="p-2.5">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={file.name}>{file.name}</p>
                            <div className="mt-1 flex items-center justify-between">
                              <span className="flex items-center gap-1.5 text-xs">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getFileCategory(file).classes}`}>
                                  {getFileCategory(file).label}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">{formatBytes(file.size_bytes)}</span>
                              </span>
                              <div className={`flex items-center gap-1 transition-opacity ${isPreviewing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyFileLink(file); }}
                                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                  title="Copy link"
                                >
                                  <Link2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); downloadSingleFile(file); }}
                                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                  title="Download"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <div className="min-w-[560px] divide-y divide-gray-200 dark:divide-gray-700">
                        {info.folders.map(folder => (
                          <button
                            key={folder.id}
                            onClick={() => loadInfo(folder.id)}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                          >
                            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                              <Folder className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm text-gray-900 dark:text-white truncate flex-1">{folder.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Folder</span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </button>
                        ))}
                        {info.files.map(file => {
                          const isSelected = selectedIds.has(file.id);
                          const isPreviewing = previewFile?.id === file.id;
                          return (
                            <div
                              key={file.id}
                              onClick={() => setPreviewFile(file)}
                              className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors ${isPreviewing ? 'bg-primary-500/5' : ''}`}
                            >
                              <button
                                onClick={(event) => { event.stopPropagation(); toggleSelect(file.id); }}
                                className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'border-gray-400 dark:border-gray-500 text-transparent hover:border-primary-500'}`}
                                title={isSelected ? 'Clear selection' : 'Select'}
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <FileThumb file={file} size="sm" />
                              <span className="text-sm text-gray-900 dark:text-white truncate flex-1">{file.name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${getFileCategory(file).classes}`}>
                                {getFileCategory(file).label}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatBytes(file.size_bytes)}</span>
                              <button
                                onClick={(event) => { event.stopPropagation(); copyFileLink(file); }}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                title="Copy link"
                              >
                                <Link2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(event) => { event.stopPropagation(); downloadSingleFile(file); }}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                title="Download"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Upload hint — only when enabled on the share link */}
                  {info.allowUpload && info.showUploadBox && (
                    <div
                      className="mt-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500 dark:text-gray-400">Drag & drop files here or click to upload to this folder</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files, currentFolderId || '');
                e.target.value = '';
              }}
            />
          </div>

          {/* Preview panel */}
          {previewFile && (
            <FilePreviewPanel file={previewFile} onClose={() => setPreviewFile(null)} />
          )}
        </div>

        <p className="text-center text-[11px] text-gray-500 dark:text-gray-600 mt-6">Powered by SwipeUp</p>
      </div>
    </div>
  );
};
